import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { connect } from "react-redux";

import ThresholdList from "./ThresholdsList";
import EmptyState from "../../../UI/State/EmptyState";
import ErrorPanel, { ErrorSize } from "../../../UI/ErrorPanel/ErrorPanel";

import { faSignal } from "@fortawesome/free-solid-svg-icons";

import * as ThresholdUtils from "../../../Utils/ThresholdUtils";
import * as AnalyticsActions from "../../../../middleware/Store/Actions/AnalyticsActions";
import LogService from "../../../../middleware/CommonSdk/LogService";

const LOG_TAG = "APPLICATION_THRESHOLD";

const Threshold = (props) => {

    const intl = useIntl();
    const [progress, setProgress] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [thresholds, setThresholds] = useState(null);
    const [analytics, setAnalytics] = useState(null);

    useEffect(() => {
        LogService.trace(LOG_TAG, "useEffect didmount getThresholds on server");

        if (props.appId && props.groupsMapping) {
            setProgress(true);
            handleRefreshThresholdsAndAnalytics(props.appId)
                .then(thresholds => {
                    setProgress(false);
                })
                .catch(errorPayload => {
                    LogService.error(LOG_TAG, "getThresholds error", errorPayload);
                    setProgress(false);
                    if (errorPayload.errorCode.toString().startsWith(403))
                        setErrorMessage(errorPayload.errorMessage);
                    else
                        setErrorMessage(intl.messages.thresholdsGenericErrorMessage);
                });
        }
    }, [props.appId, props.groupsMapping]);

    function handleRefreshThresholdsAndAnalytics() {
        LogService.trace(LOG_TAG, "handleRefreshThresholdsAndAnalytics");

        return handleGetThresholds(props.appId)
            .then(thresholds => {
                return handleGetAnalytics();
            })
            .catch(errorPayload => {
                return Promise.reject(errorPayload);
            });
    }

    function handleGetThresholds() {
        LogService.trace(LOG_TAG, "handleGetThresholds");

        return props.getThresholds(props.appId)
            .then(thresholds => {
                setThresholds(thresholds); //update here to also be updated when called from ThresholdsList (need intial server threshholds values)
                return Promise.resolve(thresholds);
            })
            .catch(errorPayload => {
                return Promise.reject(errorPayload);
            });
    }

    function handleGetAnalytics() {
        LogService.trace(LOG_TAG, "handleGetAnalytics");

        let metricPeriodDate = ThresholdUtils.getPeriodDate("currentmonth");
        return props.getAnalytics(props.appId, ThresholdUtils.getPeriodUnit("currentmonth"), metricPeriodDate.fromDay, metricPeriodDate.toDay)
            .then(analytics => {
                setAnalytics(analytics); //update here to also be updated when called from ThresholdsList (need intial server threshholds values)
                return Promise.resolve(analytics);
            })
            .catch(errorPayload => {
                return Promise.reject(errorPayload);
            });
    }

    function ThresholdError() {
        return (
            <div className="row">
                <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                    <div id="thresholds-error-content" >
                        <ErrorPanel id="server-error" size={ErrorSize.Medium}>
                            {errorMessage}
                        </ErrorPanel>
                    </div>
                </div>
            </div>
        );
    }

    function ThresholdNoData() {
        return (
            <div className="row">
                <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                    <div id="thresholds-empty-content" >
                        <EmptyState icon={faSignal} text={intl.messages.thresholdsNoAvailable} progress={progress} />
                    </div>
                </div>
            </div>
        );
    }

    function ThresholdNotes() {
        return (
            <div className="row">
                <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                    <div className="panel bg-fog mb-3">
                        {intl.formatMessage({ id: "thresholdsMessage" }, { b: chunks => <b>{chunks}</b> })}
                    </div>

                    <div className="panel bg-fog mb-3">
                        {intl.formatMessage({ id: "thresholdsMessageFree" }, { b: chunks => <b>{chunks}</b> })}
                    </div>

                    <div className="panel bg-fog mb-3">
                        {intl.formatMessage({ id: "thresholdsMessageCustom" }, { b: chunks => <b>{chunks}</b> })}
                    </div>
                </div>
            </div>
        );
    }

    let display = <EmptyState text="" progress={true} />;
    if (progress === false) {
        if (thresholds?.length > 0)
            display =
                <div>
                    <ThresholdNotes />
                    <ThresholdList
                        appId={props.appId}
                        canEdit={props.canEdit}
                        thresholds={thresholds}
                        analytics={analytics}
                        getThresholds={handleRefreshThresholdsAndAnalytics}
                        groupsMapping={props.groupsMapping} />
                </div>;
        else
            display = <ThresholdNoData />;
    }

    return (
        <div>
            {display}
            {errorMessage && <ThresholdError />}
        </div>
    )
}

Threshold.defaultProps = {
    appId: null,
    canEdit: false
}

Threshold.propTypes = {
    appId: PropTypes.string,
    canEdit: PropTypes.bool,
    groupsMapping: PropTypes.object
};

const mapDispatchToProps = dispatch => {
    return {
        getThresholds: (appId) => dispatch(AnalyticsActions.getThresholds(appId)),
        getAnalytics: (appId, period, fromDay, toDay, options) => dispatch(AnalyticsActions.getAnalytics(appId, period, fromDay, toDay, options))
    }
}

export default connect(null, mapDispatchToProps)(Threshold);